import { useEffect } from "react";

import { readFileAsDataUrl } from "../ChatView.logic";
import {
  buildPersistedComposerImageAttachments,
  filterPersistedComposerAttachmentsForImages,
} from "./ChatComposer.logic";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type {
  ComposerImageAttachment,
  PersistedComposerImageAttachment,
} from "../../composerDraftStore";
import type { DraftId } from "../../draftId";

/** The Draft store's key for a pre-thread session or a server-backed thread. */
type ComposerDraftTarget = ScopedThreadRef | DraftId;

/**
 * Keep the Draft's attachments readable after a reload.
 *
 * A `File` does not survive navigation, so every staged image is also written
 * to the Draft store as a data URL. A file the browser refuses to read keeps
 * whatever was persisted for it last time rather than dropping the attachment,
 * which is the difference between a stale thumbnail and a lost one.
 *
 * Its own hook because `ChatComposer` is at its file-size cap and this effect
 * shares nothing with the rest of the orchestrator: it reads the image list and
 * writes the Draft store, and no render depends on the result.
 */
export function useComposerImagePersistence(input: {
  readonly composerDraftTarget: ComposerDraftTarget;
  readonly composerImages: ReadonlyArray<ComposerImageAttachment>;
  readonly getComposerDraft: (
    target: ComposerDraftTarget,
  ) => { readonly persistedAttachments?: ReadonlyArray<PersistedComposerImageAttachment> } | null;
  readonly clearPersistedAttachments: (target: ComposerDraftTarget) => void;
  readonly syncPersistedAttachments: (
    target: ComposerDraftTarget,
    attachments: PersistedComposerImageAttachment[],
  ) => void;
}): void {
  const {
    composerDraftTarget,
    composerImages,
    getComposerDraft,
    clearPersistedAttachments,
    syncPersistedAttachments,
  } = input;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerImages.length === 0) {
        clearPersistedAttachments(composerDraftTarget);
        return;
      }
      const persistedForTarget = () =>
        getComposerDraft(composerDraftTarget)?.persistedAttachments ?? [];
      try {
        const serialized = await buildPersistedComposerImageAttachments({
          images: composerImages,
          existingPersistedAttachments: persistedForTarget(),
          readFileAsDataUrl,
        });
        if (cancelled) return;
        syncPersistedAttachments(composerDraftTarget, serialized);
      } catch {
        const fallbackAttachments = filterPersistedComposerAttachmentsForImages({
          images: composerImages,
          persistedAttachments: persistedForTarget(),
        });
        if (cancelled) return;
        syncPersistedAttachments(composerDraftTarget, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    composerDraftTarget,
    clearPersistedAttachments,
    composerImages,
    getComposerDraft,
    syncPersistedAttachments,
  ]);
}
